import React, { useEffect, useState, useCallback } from 'react';
import { connect } from 'react-redux';
import { BadgeIndianRupee, Check, ChevronLeft, ChevronRight, CircleAlert, CircleCheck, Clock, ShoppingBag, Undo2, Wallet, X } from 'lucide-react';
import { StorefrontService } from '../../../core/services/storefront.service';
import { StatCard } from './StatCard';

interface Props { UserReducer: any; UserID: any; }

const CURRENCY_SYMBOLS: Record<string, string> = { INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ' };
const money = (v: any, ccy = 'INR') => `${CURRENCY_SYMBOLS[ccy] || ''}${(Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const STATUS_PILL: Record<string, string> = {
    pending: 'brd-pill--warning', approved: 'brd-pill--info', paid: 'brd-pill--success', reversed: 'brd-pill--muted',
};

const STATUSES: [string, string][] = [['', 'All'], ['pending', 'Pending'], ['approved', 'Approved'], ['paid', 'Paid'], ['reversed', 'Reversed']];

const fmtDate = (v: string) => {
    if (!v) return '';
    const d = new Date(v.replace(' ', 'T'));
    return isNaN(d.getTime()) ? v : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const StorefrontEarningsComponent: React.FC<Props> = ({ UserReducer }) => {
    const brand_id = UserReducer?.UserData?.brand_id;

    const [rows, setRows] = useState<any[]>([]);
    const [summary, setSummary] = useState<any>({});
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [statusFilter, setStatusFilter] = useState('');
    const [loading, setLoading] = useState(false);
    const [updatingId, setUpdatingId] = useState<number | null>(null);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

    const load = useCallback(() => {
        if (!brand_id) return;
        setLoading(true);
        StorefrontService.getConversions({
            // Backend offset = page * limit (0-based). Send the 0-based page directly — sending
            // page+1 here offset the first page past all rows (empty table with a non-zero total).
            brand_id, status: statusFilter, page, limit: rowsPerPage,
        })
            .then((res: any) => {
                if (res?.status === 1 && res?.data?.rows) {
                    setRows(res.data.rows.conversions || []);
                    setTotal(res.data.rows.total || 0);
                    setSummary(res.data.rows.summary || {});
                } else {
                    setRows([]); setTotal(0); setSummary({});
                }
            })
            .catch(() => setAlert({ type: 'error', msg: 'Failed to load affiliate sales' }))
            .finally(() => setLoading(false));
    }, [brand_id, statusFilter, page, rowsPerPage]);

    useEffect(() => { load(); }, [load]);

    const changeStatus = (conversion_id: number, status: 'approved' | 'paid' | 'reversed') => {
        setUpdatingId(conversion_id);
        StorefrontService.updateConversionStatus({ brand_id, conversion_id, status })
            .then((res: any) => {
                if (res?.status === 1) {
                    setAlert({ type: 'success', msg: `Marked ${status}` });
                    load();
                } else {
                    setAlert({ type: 'error', msg: res?.info || 'Update failed' });
                }
            })
            .catch(() => setAlert({ type: 'error', msg: 'Update failed' }))
            .finally(() => setUpdatingId(null));
    };

    const pageCount = Math.max(1, Math.ceil(total / rowsPerPage));

    return (
        <div>
            <p className="brd-store__caption">Commissions earned by influencers driving sales to your store. Approve a pending commission to confirm it, mark it paid after payout, or reverse it on a return.</p>

            {alert && (
                <div className={`brd-banner ${alert.type === 'error' ? 'brd-banner--error' : 'brd-banner--info'}`} role="status">
                    {alert.type === 'error' ? <CircleAlert size={16} /> : <CircleCheck size={16} />}
                    <span style={{ flex: 1 }}>{alert.msg}</span>
                    <button type="button" onClick={() => setAlert(null)} aria-label="Dismiss"><X size={14} /></button>
                </div>
            )}

            {/* Summary cards */}
            <div className="brd-store-stats brd-store-stats--5">
                <StatCard icon={<Wallet size={18} />} label="Total commission" value={money(summary.total_commission)} tone="primary" />
                <StatCard icon={<Clock size={18} />} label="Pending" value={money(summary.pending_commission)} tone="warning" />
                <StatCard icon={<CircleCheck size={18} />} label="Approved" value={money(summary.approved_commission)} tone="info" />
                <StatCard icon={<BadgeIndianRupee size={18} />} label="Paid" value={money(summary.paid_commission)} tone="success" />
                <StatCard icon={<ShoppingBag size={18} />} label="Orders" value={summary.total_orders || 0} tone="violet" />
            </div>

            {/* Status filter */}
            <div className="brd-chip-scroll brd-store-filterchips" role="tablist" aria-label="Status">
                {STATUSES.map(([value, label]) => (
                    <button key={label} type="button" role="tab" aria-selected={statusFilter === value}
                        className={`brd-chip${statusFilter === value ? ' brd-chip--on' : ''}`}
                        onClick={() => { setPage(0); setStatusFilter(value); }}>
                        {label}
                    </button>
                ))}
            </div>

            <div className="brd-card brd-store-table">
                <div className="brd-store-dtable brd-store-dtable--flush">
                    <table>
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Influencer</th>
                                <th>Product</th>
                                <th>Order ID</th>
                                <th className="brd-store-dtable__num">Order</th>
                                <th className="brd-store-dtable__num">Commission</th>
                                <th>Status</th>
                                <th className="brd-store-dtable__num">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={8} className="brd-store-dtable__empty"><span className="brd-spinner" /></td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan={8} className="brd-store-dtable__empty">No affiliate sales yet.</td></tr>
                            ) : rows.map((r) => (
                                <tr key={r.conversion_id}>
                                    <td className="brd-store-dtable__nowrap">{fmtDate(r.purchased_at)}</td>
                                    <td>{r.influencer_name || `#${r.influencer_id}`}</td>
                                    <td>
                                        <span className="brd-store-table__title" title={r.product_name}>{r.product_name || '—'}</span>
                                        <span className="brd-store-table__muted">{r.source}</span>
                                    </td>
                                    <td className="brd-store-table__muted">{r.transaction_id}</td>
                                    <td className="brd-store-dtable__num">{money(r.order_value, r.currency)}</td>
                                    <td className="brd-store-dtable__num">
                                        <strong>{money(r.commission_amount, r.currency)}</strong>
                                        <span className="brd-store-table__muted" style={{ display: 'block' }}>{r.commission_percent}%</span>
                                    </td>
                                    <td><span className={`brd-pill ${STATUS_PILL[r.status] || 'brd-pill--muted'}`} style={{ textTransform: 'capitalize' }}>{r.status}</span></td>
                                    <td className="brd-store-dtable__num">
                                        {updatingId === r.conversion_id ? (
                                            <span className="brd-spinner brd-spinner--sm" />
                                        ) : (
                                            <div className="brd-store-table__actions">
                                                {r.status === 'pending' && (
                                                    <button type="button" className="brd-btn brd-btn--secondary brd-btn--sm" title="Approve" onClick={() => changeStatus(r.conversion_id, 'approved')}>
                                                        <Check size={14} /> Approve
                                                    </button>
                                                )}
                                                {r.status === 'approved' && (
                                                    <button type="button" className="brd-btn brd-btn--success brd-btn--sm" title="Mark as paid" onClick={() => changeStatus(r.conversion_id, 'paid')}>
                                                        <BadgeIndianRupee size={14} /> Paid
                                                    </button>
                                                )}
                                                {(r.status === 'pending' || r.status === 'approved') && (
                                                    <button type="button" className="brd-btn brd-btn--danger-outline brd-btn--sm" title="Reverse (return/cancel)" onClick={() => changeStatus(r.conversion_id, 'reversed')}>
                                                        <Undo2 size={14} /> Reverse
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <div className="brd-store-pager">
                    <label className="brd-store-pager__size">
                        Rows
                        <select className="brd-select" value={rowsPerPage} onChange={e => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}>
                            {[10, 20, 50].map(n => <option key={n} value={n}>{n}</option>)}
                        </select>
                    </label>
                    <span className="brd-store-pager__info">
                        {total ? `${page * rowsPerPage + 1}–${Math.min(total, (page + 1) * rowsPerPage)} of ${total}` : '0 of 0'}
                    </span>
                    <button type="button" className="brd-icon-btn" disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page"><ChevronLeft size={18} /></button>
                    <button type="button" className="brd-icon-btn" disabled={page + 1 >= pageCount} onClick={() => setPage(page + 1)} aria-label="Next page"><ChevronRight size={18} /></button>
                </div>
            </div>
        </div>
    );
};

const mapStateToProps = (state: any) => ({ UserReducer: state.UserReducer, UserID: state.LoginUserIdReducer });
export const StorefrontEarnings = connect(mapStateToProps)(StorefrontEarningsComponent);
